import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import type { RootState, AppDispatch } from '../../redux/store';
import { fetchReviewAnalysisRequest } from '../../redux/slices/reviewAnalysisSlice';
import PageHeader from '../common/PageHeader';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import EmptyState from '../common/EmptyState';
import AnalysisSummary from './AnalysisSummary';
import ReviewResponses from './ReviewResponses';

const ReviewAnalysis: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { data, loading, error } = useSelector((state: RootState) => state.reviewAnalysis);
  const [activeTab, setActiveTab] = useState<'high' | 'low'>('high');

  useEffect(() => {
    // Fetch review analysis data from URL params
    if (restaurantId) {
      dispatch(fetchReviewAnalysisRequest(restaurantId));
    }
  }, [dispatch, restaurantId]);

  if (loading) {
    return <LoadingState page="review analysis" />;
  }

  if (error) {
    return (
      <ErrorState 
        message="Failed to load review analysis" 
        error={error}
        onRetry={() => restaurantId && dispatch(fetchReviewAnalysisRequest(restaurantId))}
      />
    );
  }

  if (!data) {
    return <EmptyState message="No review analysis data available" />;
  }

  // Separate high and low rating reviews
  const highRatingReviews = data.suggested_review_responses.filter(review =>
    review.target_review_summary.includes('(5/5)') || review.target_review_summary.includes('(4/5)')
  );
  
  const lowRatingReviews = data.suggested_review_responses.filter(review =>
    review.target_review_summary.includes('(3/5)') || review.target_review_summary.includes('(2/5)') || review.target_review_summary.includes('(1/5)')
  );

  return (
    <div className="h-screen overflow-hidden flex flex-col">
      <PageHeader title="Review Analysis"  userName="Delaney" />
      
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 mt- flex-1 overflow-hidden">
        {/* Left: Analysis Summary + Operational Improvement (4 columns) */}
        <div className="lg:col-span-4 overflow-y-auto">
          <AnalysisSummary data={data} />
        </div>

        {/* Right: Review Responses (6 columns) */}
        <div className="lg:col-span-6 overflow-y-auto">
          <ReviewResponses
            highRatingReviews={highRatingReviews}
            lowRatingReviews={lowRatingReviews}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>
      </div>
    </div>
  );
};

export default ReviewAnalysis;
